// src/components/SymptomLogSection.jsx
//
// Symptom & Risk Questionnaire — patient self-service view/update.
// Used inside PatientSettingsPage as a new "Symptom Log" section, following
// the exact same pattern as the existing HealthProfileForm section.
//
// Constitution v9.7 §VIII. Question bank transcribed verbatim from
// Previu_Symptom_Questionnaire_v1.0.docx — do not paraphrase these when
// updating; option wording is what gets stored as responseText.
//
// IMPORTANT — KNOWN OVERLAP NOT RESOLVED HERE (flagged, not fixed):
// Q14 ("family heart attack before 55") and Q15 ("family Type 2 diabetes")
// ask for the same clinical facts as HealthProfileForm's existing
// FAMILY_CONDITIONS chips ("Heart attack before age 55", "Type 2 Diabetes"),
// stored in patient.medicalHistory as a parsed string — a completely
// separate mechanism from this questionnaire's patient_risk_modifiers table.
// A patient could answer these differently in the two places with nothing
// reconciling them. This component does NOT attempt to auto-populate Q14/
// Q15 from the existing chips, or vice versa — that's a product decision
// (which system is the source of truth) that needs a deliberate call, not
// something to silently resolve in this UI layer.
//
// Props:
//   patient   {id, gender, ...}  — pass usePatient()'s `profile` (the live
//             GET /patients/{id} record), not its `patient` (the thin
//             {id, firstName, lastName, phone, email} object cached from the
//             login response). The latter has no gender field, which used to
//             make isWoman below always false and silently hide the two
//             women-only questions from every patient regardless of actual
//             gender — see PatientSettingsPage.jsx's call site for the story.

import { useState, useEffect, useCallback } from 'react';
import { WOMEN_ONLY_QUESTION_CODES } from '../utils/patientProfileCompleteness';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

// ── Question bank — transcribed verbatim from the source document ──────────
const QUESTIONS = [
  { code: 'Q1', category: 'A', text: "How would you describe your energy levels lately?",
    sub: "Think about the past 2–4 weeks compared to what feels normal for you.",
    options: [
      { text: "Good — I feel like myself", severity: 'NONE' },
      { text: "A bit more tired than usual", severity: 'MONITOR' },
      { text: "Unusually fatigued most days", severity: 'FLAG' },
      { text: "Exhausted even after a full night's rest", severity: 'FLAG' },
    ] },
  { code: 'Q2', category: 'A', text: "How is your sleep? Do you wake up feeling rested?",
    options: [
      { text: "Yes, I sleep well and wake refreshed", severity: 'NONE' },
      { text: "Wake up tired, but I sleep through the night", severity: 'MONITOR' },
      { text: "Wake up several times during the night", severity: 'MONITOR' },
      { text: "Snore loudly / been told I stop breathing during sleep", severity: 'FLAG' },
    ] },
  { code: 'Q3', category: 'B', text: "Have you noticed feeling more thirsty than usual, or needing to pass urine more frequently?",
    sub: "This can sometimes be the body's quiet way of signalling changes in blood sugar.",
    options: [
      { text: "No change noticed", severity: 'NONE' },
      { text: "Slightly more than usual", severity: 'MONITOR' },
      { text: "Yes — noticeably more, most days", severity: 'FLAG' },
    ] },
  { code: 'Q4', category: 'B', text: "Have you noticed any unexplained weight changes — either gaining or losing without trying?",
    options: [
      { text: "Weight has been stable", severity: 'NONE' },
      { text: "Gradual gain over recent months", severity: 'MONITOR' },
      { text: "Unexplained weight loss", severity: 'FLAG' },
    ] },
  { code: 'Q5', category: 'C', text: "Do you ever notice any discomfort, tightness, or pressure in your chest — especially during physical activity?",
    sub: "This includes mild sensations that come and go. It's always worth telling us about.",
    options: [
      { text: "No, nothing like that", severity: 'NONE' },
      { text: "Occasionally, only during exertion", severity: 'MONITOR' },
      { text: "Yes, and it happens at rest too", severity: 'FLAG' },
    ] },
  { code: 'Q6', category: 'C', text: "Have you noticed any shortness of breath doing activities that didn't used to cause it?",
    options: [
      { text: "No change", severity: 'NONE' },
      { text: "Mild — takes me longer to recover after exertion", severity: 'MONITOR' },
      { text: "Yes, even with light activity like walking", severity: 'FLAG' },
    ] },
  { code: 'Q7', category: 'C', text: "Have you ever noticed sudden facial drooping, arm weakness, or difficulty speaking — even briefly?",
    sub: "IMPORTANT: If any of these are happening right now, call emergency services (112) immediately. Do not wait.",
    fastProtocol: true,
    options: [
      { text: "No, never", severity: 'NONE' },
      { text: "Had a brief episode in the past", severity: 'FLAG' },
      { text: "Currently experiencing this", severity: 'FLAG', emergency: true },
    ] },
  { code: 'Q8', category: 'D', text: "Any persistent bloating, upper abdominal discomfort, or feeling full very quickly when eating?",
    options: [
      { text: "Rarely or never", severity: 'NONE' },
      { text: "Occasionally — not distressing", severity: 'MONITOR' },
      { text: "Frequently, most weeks", severity: 'FLAG' },
    ] },
  { code: 'Q9', category: 'D', text: "Have you noticed any changes in your bowel habits lasting more than 3 weeks, or any blood in your stool?",
    options: [
      { text: "No change", severity: 'NONE' },
      { text: "Change in habit, but no blood", severity: 'MONITOR' },
      { text: "Blood in stool noticed", severity: 'FLAG' },
    ] },
  { code: 'Q10', category: 'E', text: "Over the past 2 weeks, how often have you felt low in mood, hopeless, or lost interest in things you usually enjoy?",
    options: [
      { text: "Not at all", severity: 'NONE' },
      { text: "A few days", severity: 'MONITOR' },
      { text: "More than half the days", severity: 'FLAG' },
      { text: "Nearly every day", severity: 'FLAG' },
    ] },
  { code: 'Q11', category: 'E', text: "Have you noticed any brain fog, memory lapses, or difficulty concentrating recently?",
    options: [
      { text: "No, sharp as usual", severity: 'NONE' },
      { text: "Occasional forgetfulness, nothing major", severity: 'MONITOR' },
      { text: "Noticeably more foggy or forgetful lately", severity: 'FLAG' },
    ] },
  { code: 'Q12', category: 'F', text: "Any persistent joint pain, swelling, or stiffness — especially in the morning?",
    options: [
      { text: "No joint issues", severity: 'NONE' },
      { text: "Mild, occasional aches", severity: 'MONITOR' },
      { text: "Regular pain affecting daily life", severity: 'FLAG' },
      { text: "Sudden intense pain in one joint (e.g. big toe, knee)", severity: 'FLAG' },
    ] },
  { code: 'Q13', category: 'F', text: "Have you noticed any muscle weakness — like difficulty climbing stairs or getting up from a chair?",
    sub: "Especially relevant if you are currently on a statin medication.",
    options: [
      { text: "No, strength feels normal", severity: 'NONE' },
      { text: "Mild difficulty, manageable", severity: 'MONITOR' },
      { text: "Clear weakness noticed", severity: 'FLAG' },
    ] },
  { code: 'Q14', category: 'G', text: "Has any close family member (parent or sibling) had a heart attack or coronary artery disease — especially before age 55?",
    overlapWarning: true,
    options: [
      { text: "No, or not that I know of", severity: 'NONE' },
      { text: "Yes, but after age 55", severity: 'MONITOR' },
      { text: "Yes — before age 55", severity: 'FLAG' },
    ] },
  { code: 'Q15', category: 'G', text: "Does Type 2 diabetes run in your close family?",
    overlapWarning: true,
    options: [
      { text: "No", severity: 'NONE' },
      { text: "Yes — grandparent, aunt or uncle", severity: 'MONITOR' },
      { text: "Yes — parent or sibling (first degree)", severity: 'FLAG' },
    ] },
  { code: 'Q16', category: 'G', text: "Do you currently smoke, or have you smoked in the past?",
    options: [
      { text: "Never smoked", severity: 'NONE' },
      { text: "Former smoker — quit more than 5 years ago", severity: 'MONITOR' },
      { text: "Former smoker — quit within the past 5 years", severity: 'FLAG' },
      { text: "Current smoker", severity: 'FLAG' },
    ] },
  { code: 'Q17', category: 'G', text: "How would you describe your physical activity on a typical week?",
    options: [
      { text: "Active — 150+ minutes structured exercise per week", severity: 'NONE' },
      { text: "Moderate — 60–150 minutes per week", severity: 'MONITOR' },
      { text: "Light — mostly walking or incidental movement", severity: 'MONITOR' },
      { text: "Mostly sedentary — desk-bound most of the day", severity: 'FLAG' },
    ] },
  { code: 'Q18', category: 'G', text: "Are you currently taking any regular medications — including supplements or Ayurvedic preparations?",
    options: [
      { text: "No regular medications", severity: 'NONE' },
      { text: "Supplements only (vitamins, protein, herbal)", severity: 'MONITOR' },
      { text: "Prescription medications", severity: 'FLAG' },
      { text: "Both prescription medications and supplements", severity: 'FLAG' },
    ] },
  { code: 'Q19', category: 'H', womenOnly: true, text: "Have you noticed any irregular periods, or have you gone through menopause?",
    options: [
      { text: "Periods regular — premenopausal", severity: 'NONE' },
      { text: "Irregular cycles", severity: 'MONITOR' },
      { text: "Postmenopausal — no bleeding", severity: 'MONITOR' },
      { text: "Postmenopausal — any bleeding noticed, even spotting", severity: 'FLAG', critical: true },
      { text: "Not applicable", severity: 'NONE' },
    ] },
  { code: 'Q20', category: 'H', womenOnly: true, text: "Have you noticed any new lumps — in the breast, neck, armpit, or groin?",
    sub: "Any new lump is worth telling us about, even if it feels small or painless.",
    options: [
      { text: "Nothing noticed", severity: 'NONE' },
      { text: "Yes — noticed a new lump", severity: 'FLAG', critical: true },
      { text: "Not applicable", severity: 'NONE' },
    ] },
];

const CATEGORY_LABELS = {
  A: 'Energy & Fatigue', B: 'Metabolic Signals', C: 'Heart & Circulation',
  D: 'Digestion & Liver', E: 'Mind & Mood', F: 'Joints, Muscles & Bones',
  G: 'Background & Confounders', H: "Women's Health",
};

const SEVERITY_STYLE = {
  NONE:    { icon: '✓', color: 'var(--green)', bg: 'var(--green-bg)' },
  MONITOR: { icon: '~', color: 'var(--amber-text)', bg: 'var(--amber-bg)' },
  FLAG:    { icon: '!', color: 'var(--red)', bg: 'var(--red-bg)' },
};

export default function SymptomLogSection({ patient }) {
  const [current, setCurrent] = useState({}); // { [questionCode]: { responseText, severity, recordedAt } }
  const [criticalFlag, setCriticalFlag] = useState({ active: false, reason: '' });
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const token = localStorage.getItem('previu_token');

  const loadCurrentState = useCallback(() => {
    if (!patient?.id) return;
    setLoading(true);
    fetch(`${API_BASE}/patients/${patient.id}/symptoms`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        const map = {};
        (data.currentAnswers || []).forEach(a => { map[a.questionCode] = a; });
        setCurrent(map);
        setCriticalFlag({
          active: !!data.criticalFlagActive,
          reason: data.criticalFlagReason || '',
        });
      })
      .catch(() => setMsg({ type: 'error', text: 'Could not load your symptom log. Please refresh.' }))
      .finally(() => setLoading(false));
  }, [patient?.id, token]);

  useEffect(() => { loadCurrentState(); }, [loadCurrentState]);

  // The womenOnly codes come from the shared list, so the profile gate cannot
  // demand answers to questions this component has decided not to ask. It did:
  // the gate required all twenty while a male patient was shown eighteen, so he
  // answered everything he could see and stayed frozen at "18 of 20" with the
  // other two nowhere on his screen.
  const isWoman = patient?.gender === 'FEMALE';
  const visibleQuestions = QUESTIONS.filter(
    q => isWoman || !WOMEN_ONLY_QUESTION_CODES.includes(q.code));
  const categories = [...new Set(visibleQuestions.map(q => q.category))];

  async function submitAnswer(question, option) {
    setSaving(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await fetch(`${API_BASE}/patients/${patient.id}/symptoms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          episode: 'AD_HOC_UPDATE',
          source: 'PATIENT_SELF_SERVICE',
          answers: [{
            questionCode: question.code,
            questionCategory: question.category,
            responseText: option.text,
            severity: option.severity,
          }],
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const map = {};
      (data.currentAnswers || []).forEach(a => { map[a.questionCode] = a; });
      setCurrent(map);
      setCriticalFlag({ active: !!data.criticalFlagActive, reason: data.criticalFlagReason || '' });
      setEditingCode(null);
      setMsg({ type: 'success', text: 'Updated.' });
    } catch (e) {
      setMsg({ type: 'error', text: 'Could not save your answer — please try again.' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>Loading your symptom log…</div>;
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'var(--font-body)',
        marginBottom: 20, lineHeight: 1.6 }}>
        Your answers here help us understand your health beyond lab results — some responses
        directly adjust how we interpret your test results. Update anytime something changes.
      </div>

      {criticalFlag.active && (
        <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius-md)',
          padding: '14px 16px', marginBottom: 20, fontSize: 13, color: 'var(--red)', lineHeight: 1.5 }}>
          <strong>A navigator will be reaching out to you.</strong> {criticalFlag.reason}
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--red)' }}>
            This can't be cleared from here — a member of our clinical team will follow up directly.
          </div>
        </div>
      )}

      {msg.text && (
        <div style={{ fontSize: 13, marginBottom: 16, color: msg.type === 'error' ? 'var(--red)' : 'var(--green)' }}>
          {msg.text}
        </div>
      )}

      {categories.map(cat => (
        <div key={cat} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)',
            marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            {cat} — {CATEGORY_LABELS[cat]}
          </div>

          {visibleQuestions.filter(q => q.category === cat).map(q => {
            const answered = current[q.code];
            const isEditing = editingCode === q.code;
            const style = answered ? SEVERITY_STYLE[answered.severity] : null;

            return (
              <div key={q.code} style={{ marginBottom: 12, padding: '12px 14px',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                background: 'var(--bg-surface)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {q.text}
                </div>
                {q.sub && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{q.sub}</div>
                )}
                {q.overlapWarning && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>
                    Note: also asked in your Health Profile family history — please keep both consistent.
                  </div>
                )}

                {!isEditing && answered && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                      color: style.color, background: style.bg }}>
                      {style.icon} {answered.responseText}
                    </span>
                    <button onClick={() => setEditingCode(q.code)}
                      style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'none',
                        border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      Update
                    </button>
                  </div>
                )}

                {!isEditing && !answered && (
                  <button onClick={() => setEditingCode(q.code)}
                    style={{ fontSize: 12, color: 'var(--brand-primary, var(--text-primary))', background: 'none',
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                      padding: '6px 12px', cursor: 'pointer' }}>
                    Answer this question
                  </button>
                )}

                {isEditing && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                    {q.options.map((opt, i) => (
                      <button key={i} disabled={saving}
                        onClick={() => {
                          if (opt.emergency) {
                            window.alert('If this is happening right now, please call 112 immediately — do not wait for this to save.');
                          }
                          submitAnswer(q, opt);
                        }}
                        style={{ textAlign: 'left', fontSize: 13, padding: '8px 12px',
                          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                          background: opt.critical ? 'var(--red-bg)' : 'var(--bg-subtle)',
                          cursor: saving ? 'default' : 'pointer' }}>
                        {opt.text}
                      </button>
                    ))}
                    <button onClick={() => setEditingCode(null)}
                      style={{ fontSize: 12, color: 'var(--text-muted)', background: 'none',
                        border: 'none', cursor: 'pointer', alignSelf: 'flex-start', marginTop: 2 }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
